import { useCallback, useEffect, useState } from "react";
import {
  FileCog,
  FileText,
  HelpCircle,
  Image,
  LayoutDashboard,
  Palette,
  Plus,
  Save,
  Settings2,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { BideBrand } from "./PublicSite";

type RecordValue = string | number | boolean | null;
type CmsRecord = Record<string, RecordValue> & { id?: string; key?: string };
type CmsData = {
  site_content: CmsRecord[];
  blog_posts: CmsRecord[];
  faq_items: CmsRecord[];
  media_assets: CmsRecord[];
  design_settings: CmsRecord[];
  admin_settings: CmsRecord[];
};
type Resource =
  | "site-content"
  | "blog-posts"
  | "faq-items"
  | "media-assets"
  | "design-settings"
  | "admin-settings";

const demoCms: CmsData = {
  site_content: [
    {
      key: "hero_eyebrow",
      group_name: "home",
      label: "Hero eyebrow",
      value: "Invoice data extraction, without the busywork",
    },
    {
      key: "hero_title",
      group_name: "home",
      label: "Hero title",
      value: "Turn invoices into clean, usable data.",
    },
    {
      key: "hero_description",
      group_name: "home",
      label: "Hero description",
      value:
        "Upload invoices in any layout. Bulk Invoice Data Extractor captures the fields you need and builds one spreadsheet.",
    },
  ],
  blog_posts: [
    {
      id: "demo-post",
      slug: "invoice-data-extraction-guide",
      title: "A practical guide to invoice data extraction",
      category: "Guide",
      excerpt:
        "What reliable extraction looks like and where review adds value.",
      content: "Invoice extraction is more than copying a total from a PDF.",
      status: "PUBLISHED",
    },
  ],
  faq_items: [
    {
      id: "demo-faq",
      question: "Do I need an account to try Bulk Invoice Data Extractor?",
      answer:
        "You can select PDFs first, then sign in before upload or processing.",
      sort_order: 10,
      published: true,
    },
  ],
  media_assets: [
    {
      id: "demo-media",
      name: "Freelancer invoice example",
      url: "/invoice-sample.png",
      alt_text: "Freelancer invoice sample number 1024",
      mime_type: "image/png",
    },
  ],
  design_settings: [
    { key: "primary_color", label: "Primary green", value: "#2f7d59" },
    { key: "paper_color", label: "Paper background", value: "#f8f5ed" },
    { key: "corner_radius", label: "Corner radius", value: "12px" },
  ],
  admin_settings: [
    { key: "PROCESSING_ENABLED", value: true },
    { key: "AI_PROCESSING_ENABLED", value: true },
    { key: "MAX_FILES_PER_DATASET", value: 300 },
    { key: "MAX_DATASETS_PER_DAY", value: 5 },
  ],
};

const tabs = [
  ["overview", LayoutDashboard, "Overview"],
  ["content", FileText, "Website copy"],
  ["blog", FileCog, "Blog posts"],
  ["faq", HelpCircle, "FAQs"],
  ["media", Image, "Images & media"],
  ["design", Palette, "Design"],
  ["functions", Settings2, "Functions & settings"],
] as const;

function valueFrom(form: FormData, key: string) {
  return String(form.get(key) ?? "").trim();
}

export function AdminCMS() {
  const [active, setActive] = useState<(typeof tabs)[number][0]>("overview");
  const [data, setData] = useState<CmsData>(demoCms);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(
    import.meta.env.VITE_DEMO_MODE === "true",
  );
  const [notice, setNotice] = useState("");
  const demo = import.meta.env.VITE_DEMO_MODE === "true";

  const load = useCallback(
    async function load() {
      if (demo) {
        setData(demoCms);
        setAuthorized(true);
        setLoading(false);
        return;
      }
      try {
        const me = await api<{ is_admin: boolean }>("/v1/me");
        if (!me.is_admin) {
          setAuthorized(false);
          setLoading(false);
          return;
        }
        setAuthorized(true);
        setData(await api<CmsData>("/v1/admin/cms"));
      } catch {
        setAuthorized(false);
      } finally {
        setLoading(false);
      }
    },
    [demo],
  );
  useEffect(() => {
    void load();
  }, [load]);

  async function save(
    resource: Resource,
    record: CmsRecord,
    payload: Record<string, RecordValue>,
  ) {
    const identity = String(record.id ?? record.key ?? "");
    if (demo) {
      const collection = resource.replaceAll("-", "_") as keyof CmsData;
      setData((current) => ({
        ...current,
        [collection]: current[collection].map((item) =>
          String(item.id ?? item.key) === identity
            ? { ...item, ...payload }
            : item,
        ),
      }));
      setNotice(
        "Saved in the local preview. Connect Supabase to persist this change.",
      );
      return;
    }
    await api(`/v1/admin/cms/${resource}/${encodeURIComponent(identity)}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
    setNotice("Saved and published to the CMS.");
    await load();
  }

  async function create(
    resource: Resource,
    payload: Record<string, RecordValue>,
  ) {
    if (demo) {
      const collection = resource.replaceAll("-", "_") as keyof CmsData;
      setData((current) => ({
        ...current,
        [collection]: [
          { id: crypto.randomUUID(), ...payload },
          ...current[collection],
        ],
      }));
      setNotice(
        "Created in the local preview. Connect Supabase to persist this item.",
      );
      return;
    }
    await api(`/v1/admin/cms/${resource}`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
    setNotice("Created successfully.");
    await load();
  }

  async function remove(resource: Resource, record: CmsRecord) {
    const identity = String(record.id ?? record.key ?? "");
    if (demo) {
      const collection = resource.replaceAll("-", "_") as keyof CmsData;
      setData((current) => ({
        ...current,
        [collection]: current[collection].filter(
          (item) => String(item.id ?? item.key) !== identity,
        ),
      }));
      setNotice("Removed from the local preview.");
      return;
    }
    await api(`/v1/admin/cms/${resource}/${encodeURIComponent(identity)}`, {
      method: "DELETE",
    });
    setNotice("Removed.");
    await load();
  }

  if (loading)
    return <main className="cms-loading">Checking administrator access…</main>;
  if (!authorized)
    return (
      <main className="cms-denied">
        <ShieldCheck />
        <h1>Administrator access required</h1>
        <p>This URL is protected by the server-side admin allowlist.</p>
        <Link to="/">Return to Bulk Invoice Data Extractor</Link>
      </main>
    );

  return (
    <main className="cms-shell">
      <aside className="cms-sidebar">
        <BideBrand />
        <div className="cms-admin-label">
          <ShieldCheck size={14} /> Administrator
        </div>
        <nav>
          {tabs.map(([id, Icon, label]) => (
            <button
              className={active === id ? "active" : ""}
              key={id}
              onClick={() => setActive(id)}
            >
              <Icon size={17} />
              {label}
            </button>
          ))}
        </nav>
        <Link to="/app">Back to workspace</Link>
      </aside>
      <section className="cms-main">
        <header>
          <div>
            <span>BULK INVOICE DATA EXTRACTOR CONTROL CENTER</span>
            <h1>{tabs.find((tab) => tab[0] === active)?.[2]}</h1>
          </div>
          <p>URL-only access · role protected</p>
        </header>
        {notice && (
          <div className="cms-notice" role="status">
            {notice}
            <button onClick={() => setNotice("")}>×</button>
          </div>
        )}
        {active === "overview" && <Overview data={data} />}{" "}
        {active === "content" && (
          <ContentEditor items={data.site_content} save={save} />
        )}{" "}
        {active === "blog" && (
          <BlogEditor
            items={data.blog_posts}
            save={save}
            create={create}
            remove={remove}
          />
        )}{" "}
        {active === "faq" && (
          <FaqEditor
            items={data.faq_items}
            save={save}
            create={create}
            remove={remove}
          />
        )}{" "}
        {active === "media" && (
          <MediaEditor
            items={data.media_assets}
            save={save}
            create={create}
            remove={remove}
          />
        )}{" "}
        {active === "design" && (
          <SettingsEditor
            resource="design-settings"
            items={data.design_settings}
            save={save}
          />
        )}{" "}
        {active === "functions" && (
          <SettingsEditor
            resource="admin-settings"
            items={data.admin_settings}
            save={save}
          />
        )}
      </section>
    </main>
  );
}

function Overview({ data }: { data: CmsData }) {
  const metrics = [
    ["Published content", data.site_content.length],
    ["Blog posts", data.blog_posts.length],
    ["FAQ entries", data.faq_items.length],
    ["Media assets", data.media_assets.length],
  ];
  return (
    <>
      <section className="cms-metrics">
        {metrics.map(([label, value]) => (
          <article key={label}>
            <small>{label}</small>
            <strong>{value}</strong>
          </article>
        ))}
      </section>
      <section className="cms-card cms-welcome">
        <ShieldCheck />
        <div>
          <h2>One protected place to run Bulk Invoice Data Extractor</h2>
          <p>
            Edit public copy, publish articles and FAQs, manage image
            references, tune the design system, and control safe operational
            settings. Source code and secrets remain outside the browser.
          </p>
        </div>
      </section>
    </>
  );
}

function ContentEditor({
  items,
  save,
}: {
  items: CmsRecord[];
  save: typeof AdminCMS.prototype extends never
    ? never
    : (
        resource: Resource,
        record: CmsRecord,
        payload: Record<string, RecordValue>,
      ) => Promise<void>;
}) {
  return (
    <section className="cms-list">
      <div className="cms-section-intro">
        <h2>Website content</h2>
        <p>Change important public copy without editing code.</p>
      </div>
      {items.map((item) => (
        <form
          key={String(item.key)}
          className="cms-row"
          onSubmit={(event) => {
            event.preventDefault();
            void save("site-content", item, {
              value: valueFrom(new FormData(event.currentTarget), "value"),
            });
          }}
        >
          <label>
            <span>{String(item.label)}</span>
            <textarea name="value" defaultValue={String(item.value)} rows={2} />
          </label>
          <button>
            <Save size={15} />
            Save
          </button>
        </form>
      ))}
    </section>
  );
}

type EditorActions = {
  items: CmsRecord[];
  save: (
    r: Resource,
    x: CmsRecord,
    p: Record<string, RecordValue>,
  ) => Promise<void>;
  create: (r: Resource, p: Record<string, RecordValue>) => Promise<void>;
  remove: (r: Resource, x: CmsRecord) => Promise<void>;
};
function BlogEditor({ items, save, create, remove }: EditorActions) {
  return (
    <section className="cms-list">
      <div className="cms-section-intro">
        <div>
          <h2>Blog posts</h2>
          <p>Create drafts, edit articles and control publication.</p>
        </div>
        <button
          onClick={() =>
            void create("blog-posts", {
              slug: `new-article-${Date.now()}`,
              title: "New article",
              category: "Guide",
              excerpt: "Add a short summary.",
              content: "Write the article here.",
              status: "DRAFT",
            })
          }
        >
          <Plus size={15} />
          New post
        </button>
      </div>
      {items.map((item) => (
        <form
          className="cms-editor-card"
          key={String(item.id)}
          onSubmit={(event) => {
            event.preventDefault();
            const f = new FormData(event.currentTarget);
            void save("blog-posts", item, {
              title: valueFrom(f, "title"),
              slug: valueFrom(f, "slug"),
              category: valueFrom(f, "category"),
              excerpt: valueFrom(f, "excerpt"),
              content: valueFrom(f, "content"),
              status: valueFrom(f, "status"),
            });
          }}
        >
          <div className="cms-two">
            <label>
              Title
              <input name="title" defaultValue={String(item.title)} />
            </label>
            <label>
              Slug
              <input name="slug" defaultValue={String(item.slug)} />
            </label>
          </div>
          <div className="cms-two">
            <label>
              Category
              <input name="category" defaultValue={String(item.category)} />
            </label>
            <label>
              Status
              <select name="status" defaultValue={String(item.status)}>
                <option>DRAFT</option>
                <option>PUBLISHED</option>
              </select>
            </label>
          </div>
          <label>
            Excerpt
            <textarea
              name="excerpt"
              defaultValue={String(item.excerpt)}
              rows={2}
            />
          </label>
          <label>
            Article
            <textarea
              name="content"
              defaultValue={String(item.content)}
              rows={7}
            />
          </label>
          <div className="cms-actions">
            <button>
              <Save size={15} />
              Save post
            </button>
            <button
              type="button"
              className="danger"
              onClick={() => void remove("blog-posts", item)}
            >
              <Trash2 size={15} />
              Delete
            </button>
          </div>
        </form>
      ))}
    </section>
  );
}

function FaqEditor({ items, save, create, remove }: EditorActions) {
  return (
    <section className="cms-list">
      <div className="cms-section-intro">
        <div>
          <h2>Frequently asked questions</h2>
          <p>Answers shown on the public landing page.</p>
        </div>
        <button
          onClick={() =>
            void create("faq-items", {
              question: "New question",
              answer: "Add the answer.",
              sort_order: items.length * 10 + 10,
              published: false,
            })
          }
        >
          <Plus size={15} />
          New FAQ
        </button>
      </div>
      {items.map((item) => (
        <form
          className="cms-editor-card"
          key={String(item.id)}
          onSubmit={(event) => {
            event.preventDefault();
            const f = new FormData(event.currentTarget);
            void save("faq-items", item, {
              question: valueFrom(f, "question"),
              answer: valueFrom(f, "answer"),
              sort_order: Number(f.get("sort_order")),
              published: f.get("published") === "on",
            });
          }}
        >
          <label>
            Question
            <input name="question" defaultValue={String(item.question)} />
          </label>
          <label>
            Answer
            <textarea
              name="answer"
              defaultValue={String(item.answer)}
              rows={3}
            />
          </label>
          <div className="cms-two">
            <label>
              Order
              <input
                type="number"
                name="sort_order"
                defaultValue={Number(item.sort_order)}
              />
            </label>
            <label className="cms-check">
              <input
                type="checkbox"
                name="published"
                defaultChecked={Boolean(item.published)}
              />
              Published
            </label>
          </div>
          <div className="cms-actions">
            <button>
              <Save size={15} />
              Save FAQ
            </button>
            <button
              type="button"
              className="danger"
              onClick={() => void remove("faq-items", item)}
            >
              <Trash2 size={15} />
              Delete
            </button>
          </div>
        </form>
      ))}
    </section>
  );
}

function MediaEditor({ items, save, create, remove }: EditorActions) {
  return (
    <section className="cms-list">
      <div className="cms-section-intro">
        <div>
          <h2>Images and media</h2>
          <p>Manage hosted image references and accessible alternative text.</p>
        </div>
        <button
          onClick={() =>
            void create("media-assets", {
              name: "New image",
              url: "https://",
              alt_text: "",
              mime_type: "image/png",
            })
          }
        >
          <Plus size={15} />
          Add image
        </button>
      </div>
      {items.map((item) => (
        <form
          className="cms-media-row"
          key={String(item.id)}
          onSubmit={(event) => {
            event.preventDefault();
            const f = new FormData(event.currentTarget);
            void save("media-assets", item, {
              name: valueFrom(f, "name"),
              url: valueFrom(f, "url"),
              alt_text: valueFrom(f, "alt_text"),
              mime_type: valueFrom(f, "mime_type"),
            });
          }}
        >
          <div className="cms-media-preview">
            {String(item.url).startsWith("/") ? (
              <img src={String(item.url)} alt="" />
            ) : (
              <Image />
            )}
          </div>
          <div>
            <label>
              Name
              <input name="name" defaultValue={String(item.name)} />
            </label>
            <label>
              Image URL
              <input name="url" defaultValue={String(item.url)} />
            </label>
            <label>
              Alternative text
              <input name="alt_text" defaultValue={String(item.alt_text)} />
            </label>
            <input
              name="mime_type"
              defaultValue={String(item.mime_type)}
              hidden
            />
          </div>
          <div className="cms-actions">
            <button>
              <Save size={15} />
              Save
            </button>
            <button
              type="button"
              className="danger icon"
              onClick={() => void remove("media-assets", item)}
            >
              <Trash2 size={15} />
            </button>
          </div>
        </form>
      ))}
    </section>
  );
}

function SettingsEditor({
  resource,
  items,
  save,
}: {
  resource: "design-settings" | "admin-settings";
  items: CmsRecord[];
  save: EditorActions["save"];
}) {
  const isFunctions = resource === "admin-settings";
  return (
    <section className="cms-list">
      <div className="cms-section-intro">
        <h2>
          {isFunctions ? "Product functions and limits" : "Design system"}
        </h2>
        <p>
          {isFunctions
            ? "Change safe runtime flags and usage limits. Secrets and source code are intentionally excluded."
            : "Adjust the brand palette, typography labels and shared geometry."}
        </p>
      </div>
      <div className="cms-settings-grid">
        {items.map((item) => (
          <form
            key={String(item.key)}
            className="cms-setting"
            onSubmit={(event) => {
              event.preventDefault();
              const raw = valueFrom(new FormData(event.currentTarget), "value");
              let value: RecordValue = raw;
              if (raw === "true" || raw === "false") value = raw === "true";
              else if (raw !== "" && !Number.isNaN(Number(raw)))
                value = Number(raw);
              void save(resource, item, { value });
            }}
          >
            <label>
              <span>{String(item.label ?? item.key)}</span>
              <small>{String(item.key)}</small>
              <input name="value" defaultValue={String(item.value)} />
            </label>
            <button>
              <Save size={14} />
              Save
            </button>
          </form>
        ))}
      </div>
    </section>
  );
}
